import React, { useCallback, useMemo } from 'react'
import { ColumnDevider, Table, TableBody, TableBodyCell, TableBodyRow, TableContainer, TableHeader, TableHeaderCell } from './table/Table'
import { SunExposerSvg } from '../svgs/Svgs'
import './SunExposer.scss'
import { convertDecimalMinutesToMMSS, get_formated_time, mergeClassNames } from '../../../utils/commonFunctions'
import CustomToolTip from '../CustomToolTip'
import { ExposureTiming, OrbitalInsightSatelliteType, OrbitStartOptionsType } from './orbital-insights-type'
import { OverlayTrigger } from 'react-bootstrap'
import { ORBIT_EXPOSER_TYPES } from '../../simulate/constants'
import { DurationType } from './OrbitalInsights'
import DurationToggleButton from './DurationToggleButton'
import { getOrbitalInsightDetails } from './helperFunction'

interface SunExposerProps {
    data: OrbitalInsightSatelliteType | null
    durationType: DurationType
    setDurationType: (durationType: DurationType) => void
    getDuration: (duration: number) => string
    orbitStartOption?: OrbitStartOptionsType
}

const SunExposer = ({ data, durationType, setDurationType, getDuration, orbitStartOption = 'Start Time' }: SunExposerProps) => {
    const Orbits = useMemo(() => data ? getOrbitalInsightDetails(data, orbitStartOption) : [], [data, orbitStartOption])

    const calculateWidthPercent = useCallback((arr: ExposureTiming[], currentIndex: number): string => {
        if (!arr || arr.length === 0) return "0%";
        if (currentIndex < 0 || currentIndex >= arr.length) return "0%";

        const firstStart = arr[0].startTime;
        const lastEnd = arr[arr.length - 1].endTime;

        const totalDuration = lastEnd - firstStart;
        if (totalDuration <= 0) return "0%";

        const { duration } = arr[currentIndex];
        const width = (duration / totalDuration) * 100;

        return `${width}%`;
    }, []);

    return (
        <div className='insights__modal__body'>
            <div className='insights__modal__body__header'>
                <span className='insights__modal__body__header__text'>Sun exposure</span>
                <div className='insights__modal__body__header__badge'>
                    <span className='badge__text'>Orbit Duration</span>
                    <span className='badge__number'>{data?.orbitalPeriod ? convertDecimalMinutesToMMSS(data?.orbitalPeriod / 60) : 'N/A'}</span>
                </div>
            </div>
            <TableContainer className='h-90'>
                <Table>
                    <TableHeader>
                        <TableHeaderCell cellType='extra__small'>Orbit</TableHeaderCell>
                        <TableHeaderCell cellType='medium'>Start Time</TableHeaderCell>
                        <TableHeaderCell cellType='flexible'>Duration</TableHeaderCell>
                        <DurationToggleButton durationType={durationType} setDurationType={setDurationType} />
                    </TableHeader>
                    <TableBody>
                        <ColumnDevider style={{ left: '55px' }} />
                        <ColumnDevider style={{ left: '235px' }} />
                        {Orbits.map((orbit) => {
                            const orbitExposerType = orbit?.exposureTimings?.[0]?.exposureType || orbit?.orbitStartExposure || ORBIT_EXPOSER_TYPES.SUNLIT
                            return (
                                <TableBodyRow key={orbit.orbitNumber}>
                                    <TableBodyCell cellType='extra__small'>{orbit?.orbitNumber}</TableBodyCell>
                                    <TableBodyCell cellType='medium'>
                                        <CustomToolTip title={get_formated_time(orbit.orbitStartTime)}>{get_formated_time(orbit.orbitStartTime)}</CustomToolTip>
                                        <div className={`exposure__icon__container`}>
                                            <CustomToolTip title={orbitExposerType}>
                                                <SunExposerSvg type={orbitExposerType} />
                                            </CustomToolTip>
                                        </div>
                                    </TableBodyCell>
                                    <TableBodyCell cellType='flexible'>
                                        <div className='exposure__bar__container'>
                                            {(orbit?.exposureTimings || []).map((exposer, index) => (
                                                // Constrain tooltip within the scrollable table body
                                                // so it doesn't overflow outside the grid area.
                                                <OverlayTrigger key={exposer?.startTime} trigger={['hover', 'focus']}
                                                    placement="bottom"
                                                    overlay={
                                                        <div className='exposer__overlay__container'>
                                                            <div className='exposer__overlay__container__details__section'>
                                                                <div className='details__icon__container'>
                                                                    <SunExposerSvg type={exposer?.exposureType} />
                                                                </div>
                                                                <div className='details__label__container'>{exposer?.exposureType}</div>
                                                            </div>
                                                            <div className='exposer__overlay__container__details__section'>
                                                                <div className='details__icon__container'>ST</div>
                                                                <div className='details__label__container'>{get_formated_time(exposer?.startTime)}</div>
                                                            </div>
                                                            <div className='exposer__overlay__container__details__section'>
                                                                <div className='details__icon__container'>ET</div>
                                                                <div className='details__label__container'>{get_formated_time(exposer?.endTime)}</div>
                                                            </div>
                                                        </div>
                                                    }>
                                                    <div className={mergeClassNames(`exposure__bar ${exposer?.exposureType === ORBIT_EXPOSER_TYPES.SUNLIT && 'sunlit'}`)}
                                                        style={{ width: calculateWidthPercent(orbit?.exposureTimings, index) }}
                                                    >{getDuration(exposer.duration)}</div>
                                                </OverlayTrigger>
                                            ))}
                                        </div>
                                    </TableBodyCell>
                                </TableBodyRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}

export default SunExposer